/**
 * Map Panel Plugin
 *
 * Registers the interactive map visualization as a side-panel plugin.
 * Wraps the existing mapView/loader modules without modifying them,
 * and exposes the plugin contract required by the panel registry.
 */

import { registerPanel } from '../registry.js';
import { mountMap } from './mapView.js';
import { injectLeafletCSSInto } from './loader.js';
import { IconMapPin } from '../../components/Icons.js';

registerPanel({
  type: 'map',

  icon: IconMapPin,

  label: (t) => t('map_available'),

  /**
   * Determines whether the map panel is active based on the widget configuration.
   *
   * @param {Object} config - The global widget configuration.
   * @returns {boolean} True if maps are not explicitly disabled.
   */
  isEnabled(config) {
    return config?.map?.enabled !== false;
  },

  /**
   * Lifecycle hook invoked once before the panel content is mounted.
   * Injects Leaflet CSS into the shadow root so tile styles resolve correctly.
   *
   * @param {ShadowRoot} shadowRoot - The host shadow DOM reference.
   */
  onOpen(shadowRoot) {
    injectLeafletCSSInto(shadowRoot);
  },

  /**
   * Mounts the map visualization into the given container element.
   * Returns the imperative lifecycle handle with destroy() and update() methods.
   *
   * @param {HTMLElement} container - The DOM element to render the map into.
   * @param {Object} data - Location data payload from the API.
   * @param {Object} ctx - Context object with `t` (i18n) and `config`.
   * @returns {{ destroy: Function, update: Function }}
   */
  mount(container, data, { t, config }) {
    return mountMap(container, data, { t, config });
  }
});
